// ─── Cross-chain guard ──────────────────────────────────────────────────
//
// Quest and task ids are per-contract counters (`++_questCounter` in
// QuestManager.sol, `++_taskCounter` in TaskManager.sol), so they are only
// unique WITHIN a chain: quest 42 on Amoy and quest 42 on Polygon are
// unrelated objects holding different money.
//
// Rows carry the chain they were created on (`quests.chain_id`, added by
// 20260330000004 explicitly "for testnet/mainnet data separation"). Now that
// the active chain is switchable at runtime and switching preserves the
// current URL, a row reached from a stale route can outlive the chain it
// belongs to -- so every on-chain call must check the row's own chain before
// handing its id to a contract.

import { getChainConfig } from "@/config/contracts";
import { getActiveChainId, getSupportedChain } from "@/config/activeChain";

export class ChainMismatchError extends Error {
  readonly rowChainId: number;
  readonly activeChainId: number;

  constructor(subject: string, rowChainId: number, activeChainId: number) {
    super(
      `This ${subject} lives on ${chainLabel(rowChainId)}, but the app is running on ${chainLabel(activeChainId)}. Switch networks to act on it.`,
    );
    this.name = "ChainMismatchError";
    this.rowChainId = rowChainId;
    this.activeChainId = activeChainId;
  }
}

export function chainLabel(chainId: number): string {
  const known = getSupportedChain(chainId);
  if (!known) return `chain ${chainId}`;
  try {
    return getChainConfig(chainId).name;
  } catch {
    return known.fallback;
  }
}

/**
 * Throw unless `rowChainId` is the chain this page load is running against.
 *
 * A null/undefined chain id means the row was never pinned to a chain (an
 * older row, or one that has not been on-chain yet) -- nothing to contradict,
 * so it passes. Callers that are about to CREATE on-chain state should pass
 * the row's id anyway: once it exists on one chain, this stops it being acted
 * on from another.
 */
export function assertOnActiveChain(
  rowChainId: number | null | undefined,
  subject = "record",
): void {
  if (rowChainId == null) return;
  const active = getActiveChainId();
  if (rowChainId === active) return;
  throw new ChainMismatchError(subject, rowChainId, active);
}

/**
 * The RPC the app reads through answers as a different chain than the one it
 * was configured for.
 *
 * Contract addresses are per-chain, so a read sent to the wrong chain finds no
 * code at the address and comes back as empty `0x` -- which ethers surfaces as
 * an opaque BAD_DATA decode failure that reads like a flaky network. Prod hit
 * exactly this: `VITE_POLYGON_RPC_URL` (the Amoy override Privy's embedded
 * wallet uses, see config/privy.ts) was set to a Polygon MAINNET endpoint, so
 * every Testnet ledger read failed as "couldn't reach the network".
 */
export class RpcChainMismatchError extends Error {
  readonly expectedChainId: number;
  readonly rpcChainId: number;

  constructor(expectedChainId: number, rpcChainId: number) {
    super(
      `The RPC for ${chainLabel(expectedChainId)} answers as ${chainLabel(rpcChainId)}. Check the RPC URL configured for this network.`,
    );
    this.name = "RpcChainMismatchError";
    this.expectedChainId = expectedChainId;
    this.rpcChainId = rpcChainId;
  }
}
